import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { API_BASE } from '../config';

export type OnboardingRequirement = 'profile' | 'resume' | 'targeting' | 'documents' | 'practiceTrack';

const REQUIREMENT_META: Record<OnboardingRequirement, { label: string; href: string }> = {
  profile: { label: 'Complete your career profile', href: '/settings' },
  resume: { label: 'Upload a resume', href: '/resumes' },
  targeting: { label: 'Evaluate a role', href: '/job-targeting' },
  documents: { label: 'Generate application documents', href: '/job-targeting' },
  practiceTrack: { label: 'Track an application', href: '/tracker' },
};

interface Props {
  requires: OnboardingRequirement[];
  children: React.ReactNode;
}

/**
 * Gates a page behind onboarding prerequisites. Instead of letting a page silently error
 * when the user has no profile/resume yet, it renders a clear CTA to finish the missing step.
 */
const OnboardingGate: React.FC<Props> = ({ requires, children }) => {
  const [status, setStatus] = useState<Record<string, boolean> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/stats/onboarding`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => setStatus(data))
      .catch(() => setStatus({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-gray-500 dark:text-slate-400 p-6">Loading…</div>;
  }

  const missing = requires.filter((r) => !status?.[r]);
  if (missing.length === 0) {
    return <>{children}</>;
  }

  const first = REQUIREMENT_META[missing[0]];

  return (
    <div className="max-w-xl mx-auto bg-white dark:bg-navy-800 border border-gray-200 dark:border-slate-800 rounded-xl p-8 text-center mt-6">
      <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <Lock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Finish onboarding first</h2>
      <p className="text-sm text-gray-600 dark:text-slate-400 mt-2">
        This step needs {missing.length > 1 ? 'a few things' : 'one more thing'} before you can use it.
      </p>
      <ul className="text-sm text-gray-700 dark:text-slate-300 mt-4 space-y-1">
        {missing.map((r) => (
          <li key={r}>• {REQUIREMENT_META[r].label}</li>
        ))}
      </ul>
      <Link
        to={first.href}
        className="inline-flex items-center space-x-2 mt-6 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all"
      >
        <span>{first.label}</span>
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default OnboardingGate;
